import { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../services/api';

export interface RetailPrice {
  key: string; group: 'automation' | 'manual'; nameTr: string; nameEn: string; periodMonths: number;
  usd: number; try: number; version: string; updatedAt: string; initialExchangeRate: number; initialRateDate: string;
}
export interface RetailCatalog { paymentCurrency: 'USD'; prices: RetailPrice[] }
let cached: RetailCatalog | null = null;
let checkedAt = 0;
let pending: Promise<RetailCatalog> | null = null;
const listeners = new Set<() => void>();
export function invalidateRetailPrices() { cached = null; checkedAt = 0; listeners.forEach(notify => notify()); }
async function load(force = false) {
  if (!force && cached && Date.now() - checkedAt < 30_000) return cached;
  if (!pending) pending = axios.get<RetailCatalog>(`${api.defaults.baseURL}/public/pricing/catalog`, { timeout: 10000, withCredentials: false })
    .then(({ data }) => {
      if (data.paymentCurrency !== 'USD' || !Array.isArray(data.prices) || !data.prices.length || data.prices.some(p => !p.key || !Number.isFinite(p.usd) || !Number.isFinite(p.try) || p.usd < 0 || p.try < 0)) throw new Error('Invalid price catalog');
      cached = data; checkedAt = Date.now(); listeners.forEach(notify => notify()); return data;
    }).finally(() => { pending = null; });
  return pending;
}
export function useRetailPrices() {
  // Load after mount so static HTML and the first browser render stay consistent.
  const [catalog, setCatalog] = useState<RetailCatalog | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const sync = () => { if (active) setCatalog(cached); };
    const refresh = (force = false) => void load(force).then(data => { if (active) { setCatalog(data); setError(false); } })
      .catch(() => { if (active) { setError(true); setCatalog(null); } });
    listeners.add(sync); refresh(attempt > 0);
    const timer = window.setInterval(refresh, 30_000);
    const focus = () => refresh(true);
    window.addEventListener('focus', focus);
    return () => { active = false; listeners.delete(sync); window.clearInterval(timer); window.removeEventListener('focus', focus); };
  }, [attempt]);
  const price = (key: string) => catalog?.prices.find(p => p.key === key);
  const planAmount = (plan: string) => plan === 'Free' ? 0 : price('plan:' + plan)?.usd ?? Number.NaN;
  return { catalog, price, planAmount, error, retry: () => setAttempt(value => value + 1) };
}
